import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { AlertCircle, AlignCenter, AlignJustify, AlignLeft, AlignRight, Bold,
  CloudOff, Indent, Italic, Outdent, RotateCcw, Underline, Heading1, Heading2, Heading3, List, ListOrdered, Quote, Highlighter, Check, MessageCircle, BookOpen, X, WandSparkles, Loader2, PlusCircle, ArrowUpDown, Merge, Type, Layers, ChevronDown, CornerDownLeft } from "lucide-react";
import NumaLogo from "@/components/NumaLogo";
import NumaSelect from "@/components/ui/numa-select";
import { ACTIONS as AI_READINGS } from "@/components/NumaPanel";
import AiBadge from "@/components/AiBadge";
import NumaMascot from "@/components/NumaMascot";
import { theses as thesesApi, numa as numaApi } from "@/lib/api";
import { useLLM } from "@/contexts/LLMContext";
import { sanitizeHtml } from "@/lib/sanitize";
import { clearDraft, describeAge, pruneDrafts, readDraft, saveDraft, sameContent } from "@/lib/drafts";
import ImproveTextModal from "@/components/ImproveTextModal";
import InlineSuggestionsPanel from "@/components/InlineSuggestionsPanel";
import { toast } from "sonner";

const cmd = (c, val = null) => document.execCommand(c, false, val);

/**
 * Paragraph styles offered in the toolbar.
 *
 * These map one-to-one onto what the DOCX exporter understands, so a heading
 * chosen here arrives in Word as a real Heading style - which is what makes the
 * generated table of contents work - and a quotation arrives as a quotation.
 */
/**
 * Typefaces offered in the toolbar.
 *
 * All of them exist on Windows and macOS without being installed, so a
 * document keeps its look on the supervisor's machine as well as the author's.
 * Each entry carries a full stack, because a font missing on one platform
 * should fall back to something of the same kind rather than to the default.
 */
const FONTS = [
  { value: "'Times New Roman', Times, serif", label: "Times New Roman" },
  { value: "Palatino, 'Palatino Linotype', 'Book Antiqua', serif", label: "Palatino" },
  { value: "Garamond, 'EB Garamond', 'Apple Garamond', serif", label: "Garamond" },
  { value: "Georgia, 'Times New Roman', serif", label: "Georgia" },
  { value: "Cambria, Georgia, serif", label: "Cambria" },
  { value: "Arial, Helvetica, sans-serif", label: "Arial" },
  { value: "Calibri, Carlito, 'Segoe UI', sans-serif", label: "Calibri" },
  { value: "Helvetica, Arial, sans-serif", label: "Helvetica" },
  { value: "Verdana, Geneva, sans-serif", label: "Verdana" },
  { value: "'Courier New', Courier, monospace", label: "Courier New" },
];

// In points, the unit Word shows and the unit the DOCX exporter writes.
const FONT_SIZES = ["8", "9", "10", "11", "12", "14", "16", "18", "20", "24", "28", "32"];

const LINE_SPACINGS = [
  { value: "1", label: "Interlinea 1" },
  { value: "1.15", label: "Interlinea 1,15" },
  { value: "1.5", label: "Interlinea 1,5" },
  { value: "2", label: "Interlinea 2" },
];

// What the editor stylesheet actually renders when nothing is set, so the
// toolbar reports the truth rather than a plausible-looking "1".
const DEFAULT_LINE_SPACING = "1.5";
const DEFAULT_FONT = FONTS[0].value;
const DEFAULT_SIZE = "12";

// Elements that count as a paragraph-level container in the editor.
const BLOCK_TAGS = new Set([
  "P", "H1", "H2", "H3", "H4", "BLOCKQUOTE", "UL", "OL", "LI", "PRE", "DIV", "HR", "TABLE", "SECTION",
]);

const PARAGRAPH_STYLES = [
  { id: "P", label: "Corpo", hint: "Corpo del testo" },
  { id: "H1", label: "Titolo 1", hint: "Titolo di primo livello" },
  { id: "H2", label: "Titolo 2", hint: "Titolo di secondo livello" },
  { id: "H3", label: "Titolo 3", hint: "Titolo di terzo livello" },
  { id: "BLOCKQUOTE", label: "Citazione", hint: "Citazione in blocco" },
];

/** The block tag the caret currently sits in, so the selector reflects reality. */
function currentBlockTag() {
  const selection = window.getSelection?.();
  let node = selection?.anchorNode;
  while (node && node.nodeType !== 1) node = node.parentNode;
  while (node && !/^(P|H1|H2|H3|H4|BLOCKQUOTE|LI|DIV)$/.test(node.tagName || "")) {
    if (node.classList?.contains("numa-editor")) return "P";
    node = node.parentNode;
  }
  const tag = node?.tagName || "P";
  return tag === "DIV" ? "P" : tag;
}

function ToolbarButton({ icon: Icon, onClick, label, testid, active = false }) {
  return (
    <button
      type="button"
      data-testid={testid}
      // mousedown + preventDefault keeps the text selection alive: a plain
      // click moves focus to the button first and the selection collapses.
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      aria-pressed={active}
      title={label}
      className={`flex h-9 w-9 items-center justify-center rounded-md transition-colors ${
        active ? "bg-brand-soft text-brand" : "text-ink-muted hover:bg-surface-2 hover:text-ink"
      }`}
    >
      <Icon className="h-4 w-4" />
      <span className="sr-only">{label}</span>
    </button>
  );
}

function stripHtml(html) {
  const d = document.createElement("div");
  d.innerHTML = sanitizeHtml(html);
  return d.innerText || "";
}

function escapeHtml(value = "") {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

/**
 * Strip the page furniture before a chapter is stored.
 *
 * Pagination writes a top margin onto the block that opens each page so the
 * text lands on the right sheet. That spacing describes this window at this
 * width, not the document, and saving it would bake one screen's layout into
 * the text - and then add to it on the next pass.
 */
function cleanForSave(html = "") {
  if (!html.includes("data-numa-page")) return html;
  const holder = document.createElement("div");
  holder.innerHTML = html;
  for (const element of holder.querySelectorAll("[data-numa-page]")) {
    element.style.marginTop = "";
    if (!element.getAttribute("style")) element.removeAttribute("style");
    element.removeAttribute("data-numa-page");
  }
  return holder.innerHTML;
}

/**
 * Page boundaries declared by an imported document.
 *
 * Only a lower bound: what the measurement finds is what the text does now,
 * at this width, in this typeface.
 */
function getPageCount(html = "") {
  if (!html) return 1;
  const doc = new DOMParser().parseFromString(html, "text/html");
  const importedPages = doc.querySelectorAll(".numa-import-page, [data-page]").length;
  if (importedPages > 0) return importedPages;
  const plainLength = (doc.body?.innerText || "").trim().length;
  return Math.max(1, Math.ceil(plainLength / 2600));
}

export default function Editor({
  thesis, chapter, onChapterUpdate, onNeedHelp, passage, onChapterFocus, onRunAi,
}) {
  const { ready: llmReady, ensureKey } = useLLM();
  const [blockStyle, setBlockStyle] = useState("P");
  const [alignment, setAlignment] = useState("left");
  const [lineSpacing, setLineSpacing] = useState(DEFAULT_LINE_SPACING);
  const [fontFamily, setFontFamily] = useState(DEFAULT_FONT);
  const [fontSize, setFontSize] = useState(DEFAULT_SIZE);
  const [structureMenuOpen, setStructureMenuOpen] = useState(false);
  // The surface holding the caret. Every formatting command already works
  // through this, so pointing it at whichever chapter has focus was enough to
  // make the toolbar work across the whole document.
  const ref = useRef(null);
  const surfaces = useRef({});
  const columnRef = useRef(null);
  const saveTimer = useRef(null);
  const citationTimer = useRef(null);
  const idleTimer = useRef(null);
  const selectionRange = useRef(null);
  const renderedChapterId = useRef(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  // "saved" | "saving" | "unsaved" | "offline"
  const [saveState, setSaveState] = useState("saved");
  const [recoverable, setRecoverable] = useState(null);
  const pendingHtml = useRef(null);
  const [improveOpen, setImproveOpen] = useState(false);
  const [improveSelection, setImproveSelection] = useState(null);
  const [currentText, setCurrentText] = useState("");
  const [citationSuggestions, setCitationSuggestions] = useState([]);
  const [citationLoading, setCitationLoading] = useState(false);
  const [helpBubble, setHelpBubble] = useState(false);
  // One offer per section: a prompt that keeps coming back is an interruption.
  const helpBubbleSpent = useRef(false);
  // Off until asked for. These call a model, so leaving them on meant either a
  // key dialog nobody asked for, or - with no key - a feature that silently
  // never produced anything and could not be understood from the outside.
  const [assistedWritingEnabled, setAssistedWritingEnabled] = useState(false);
  const [pageCount, setPageCount] = useState(1);
  // One rectangle per page, drawn behind the text at the measured boundaries.
  const [sheets, setSheets] = useState([{ top: 0, height: 0 }]);

  const chapterId = chapter?.id;
  const chapterContent = chapter?.content || "";
  const allChapters = useMemo(
    () => [...(thesis?.chapters || [])].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [thesis?.chapters]
  );

  useEffect(() => {
    let changed = false;
    for (const item of allChapters) {
      const surface = surfaces.current[item.id];
      if (!surface) continue;
      const focused = document.activeElement === surface || surface.contains(document.activeElement);
      // While someone is typing in a surface, leave it alone: rewriting it
      // would move their caret to the start on every autosave round trip.
      if (focused) continue;
      // Content arrives from the model, from an imported DOCX or from the
      // stored draft; none of it is trusted markup.
      const safeHtml = sanitizeHtml(item.content || "");
      if ((surface.innerHTML || "") !== safeHtml) {
        surface.innerHTML = safeHtml;
        changed = true;
      }
    }
    if (chapterId && renderedChapterId.current !== chapterId) {
      helpBubbleSpent.current = false;
      setHelpBubble(false);
      renderedChapterId.current = chapterId;
    }
    if (changed || !currentText) {
      setCurrentText(stripHtml(chapterContent));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allChapters, chapterId, chapterContent]);

  // Choosing a chapter in the sidebar brings it to the top of the view - but
  // not on arrival: scrolling to the first chapter on load hides the heading
  // and the toolbar above it, so the page opens halfway down itself.
  const firstLayout = useRef(true);
  useEffect(() => {
    if (!chapterId) return;
    if (firstLayout.current) {
      firstLayout.current = false;
      return;
    }
    const surface = surfaces.current[chapterId];
    if (!surface) return;
    const focused = document.activeElement === surface || surface.contains(document.activeElement);
    if (focused) return;
    const container = surface.closest("[data-chapter-id]") || surface;
    container.scrollIntoView({ behavior: "smooth", block: "start" });
    ref.current = surface;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterId]);

  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (citationTimer.current) clearTimeout(citationTimer.current);
    if (idleTimer.current) clearTimeout(idleTimer.current);
  }, []);

  useEffect(() => {
    if (!assistedWritingEnabled) {
      if (citationTimer.current) clearTimeout(citationTimer.current);
      setCitationSuggestions([]);
      setCitationLoading(false);
    }
  }, [assistedWritingEnabled]);

  /**
   * Save one chapter's text.
   *
   * The chapter is named explicitly rather than taken from the active one:
   * with every chapter on screen at once, the text being typed belongs to
   * whichever surface has the caret, and that is known before React has
   * finished moving the selection in state.
   */
  const persist = useCallback(async (html, chapterId = null) => {
    const targetId = chapterId || chapter?.id;
    if (!thesis || !targetId) return;
    setSaving(true);
    setSaveState("saving");
    pendingHtml.current = html;
    try {
      const updated = await thesesApi.updateChapter(thesis.thesis_id, targetId, { content: html });
      onChapterUpdate?.(updated);
      setSavedAt(new Date());
      // The server now holds this text, so the local safety copy is redundant.
      if (pendingHtml.current === html) {
        clearDraft(thesis.thesis_id, targetId);
        pendingHtml.current = null;
        setSaveState("saved");
      }
    } catch (e) {
      // Never silent: the local copy is what protects the work, and the author
      // has to know the server did not receive it.
      console.error("Salvataggio non riuscito", e);
      setSaveState(navigator.onLine ? "unsaved" : "offline");
    } finally {
      setSaving(false);
    }
  }, [thesis, chapter, onChapterUpdate]);

  /** Retry whatever is still only in the local copy. */
  const retrySave = useCallback(() => {
    const html = pendingHtml.current ?? cleanForSave(ref.current?.innerHTML || "");
    if (html) persist(html);
  }, [persist]);

  // A dropped connection is the case the local copy exists for; when it comes
  // back, push the pending text without waiting for the next keystroke.
  useEffect(() => {
    const onOnline = () => {
      if (pendingHtml.current) retrySave();
    };
    const onOffline = () => {
      if (pendingHtml.current) setSaveState("offline");
    };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [retrySave]);

  // Last line of defence: warn before leaving with text the server lacks.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!pendingHtml.current) return undefined;
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  // On opening a section, a local copy that differs from the stored text means
  // a previous session ended before its save landed.
  useEffect(() => {
    pruneDrafts();
    if (!thesis?.thesis_id || !chapter?.id) {
      setRecoverable(null);
      return;
    }
    const draft = readDraft(thesis.thesis_id, chapter.id);
    if (draft && !sameContent(draft.html, chapter.content || "")) {
      setRecoverable(draft);
    } else {
      if (draft) clearDraft(thesis.thesis_id, chapter.id);
      setRecoverable(null);
    }
    setSaveState("saved");
    pendingHtml.current = null;
  }, [thesis?.thesis_id, chapter?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const recoverDraft = useCallback(() => {
    if (!recoverable || !ref.current) return;
    const safe = sanitizeHtml(recoverable.html);
    ref.current.innerHTML = safe;
    setCurrentText(ref.current.innerText || "");
    setRecoverable(null);
    persist(safe);
    toast.success("Modifiche ripristinate");
  }, [recoverable, persist]);

  const discardDraft = useCallback(() => {
    if (thesis?.thesis_id && chapter?.id) clearDraft(thesis.thesis_id, chapter.id);
    setRecoverable(null);
  }, [thesis?.thesis_id, chapter?.id]);

  const restructureChapter = useCallback(async (action, newTitle) => {
    if (!thesis || !chapter) return;
    try {
      const payload = { action };
      if (newTitle) payload.title = newTitle;
      const res = await thesesApi.restructureChapter?.(thesis.thesis_id, chapter.id, payload);
      if (res) {
        onChapterUpdate?.(res);
        toast.success("Struttura aggiornata");
      }
    } catch (e) {
      toast.error("Errore nell'aggiornamento della struttura");
    }
    setStructureMenuOpen(false);
  }, [thesis, chapter, onChapterUpdate]);

  const saveCurrentSelection = () => {
    const selection = window.getSelection?.();
    if (!selection || !selection.rangeCount || !ref.current) return;
    const node = selection.anchorNode;
    if (node && ref.current.contains(node)) {
      selectionRange.current = selection.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    const selection = window.getSelection?.();
    if (!selection || !selectionRange.current) return false;
    selection.removeAllRanges();
    selection.addRange(selectionRange.current);
    return true;
  };

  /** Apply a paragraph style to the block the caret is in. */
  const applyBlockStyle = (styleId) => {
    if (!ref.current) return;
    ref.current.focus();
    restoreSelection();
    cmd("formatBlock", styleId === "P" ? "P" : styleId);
    setBlockStyle(styleId);
    const updated = sanitizeHtml(cleanForSave(ref.current.innerHTML || ""));
    ref.current.innerHTML = updated;
    setCurrentText(ref.current.innerText || "");
    persist(updated);
  };

  /**
   * Insert an empty paragraph after the current block.
   *
   * Pressing Enter inside a heading continues in that heading; this gives an
   * explicit way to start ordinary body text underneath, which is what the
   * author almost always wants next.
   */
  const insertParagraphBelow = () => {
    if (!ref.current) return;
    ref.current.focus();
    restoreSelection();

    const selection = window.getSelection?.();
    let node = selection?.anchorNode;
    while (node && node.parentNode !== ref.current) {
      if (!node.parentNode || node === ref.current) break;
      node = node.parentNode;
    }

    const paragraph = document.createElement("p");
    paragraph.appendChild(document.createElement("br"));
    if (node && node.parentNode === ref.current) {
      node.after(paragraph);
    } else {
      ref.current.appendChild(paragraph);
    }

    const range = document.createRange();
    range.setStart(paragraph, 0);
    range.collapse(true);
    selection?.removeAllRanges();
    selection?.addRange(range);
    selectionRange.current = range.cloneRange();
    setBlockStyle("P");

    const updated = cleanForSave(ref.current.innerHTML || "");
    setCurrentText(ref.current.innerText || "");
    persist(updated);
  };

  /** The block element the caret sits inside, for paragraph-level styling. */
  /**
   * Show the passage a review finding is about.
   *
   * Walks the text nodes rather than searching the markup, because the finding
   * quotes the text as the reader sees it - tags gone, whitespace collapsed -
   * and that is exactly what the nodes hold between them.
   */
  const showPassage = useCallback((wanted) => {
    const root = ref.current;
    if (!root || !wanted) return false;
    const target = (wanted || "").replace(/^…|…$/g, "").trim();
    if (target.length < 3) return false;

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes = [];
    let flat = "";
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      nodes.push({ node, start: flat.length });
      flat += node.textContent;
    }

    /*
      La ricerca ignora la punteggiatura, non solo gli spazi.

      Il rilievo cita il testo come lo legge chi lo controlla - parole, senza
      virgole né apostrofi - mentre nella pagina la stessa frase ha la virgola
      che l'autore ci ha messo. Cercandola alla lettera non si trovava mai, e
      «Vedi nel testo» apriva la sezione giusta lasciando il passaggio da
      cercare a occhio. Qui si confrontano solo lettere e cifre, tenendo per
      ciascuna il punto esatto in cui si trova: quello serve per evidenziare.
    */
    const significativi = [];
    for (let i = 0; i < flat.length; i += 1) {
      const c = flat[i].toLowerCase();
      if (/\s/.test(c)) {
        if (significativi.length && significativi[significativi.length - 1].c !== " ") {
          significativi.push({ c: " ", at: i });
        }
      } else if (/[\p{L}\p{N}]/u.test(c)) {
        significativi.push({ c, at: i });
      }
    }
    const scorrevole = significativi.map((x) => x.c).join("");
    const cercato = target
      .toLowerCase()
      .replace(/[^\p{L}\p{N}\s]/gu, "")
      .replace(/\s+/g, " ")
      .trim();
    if (!cercato) return false;

    let dove = scorrevole.indexOf(cercato);
    let quanto = cercato.length;
    if (dove < 0) {
      // Il passaggio può essere stato ritoccato dopo la revisione: le prime
      // parole bastano a portarci sopra, ed è meglio del nulla.
      const inizio = cercato.split(" ").slice(0, 8).join(" ");
      if (inizio.length >= 12) {
        dove = scorrevole.indexOf(inizio);
        quanto = inizio.length;
      }
    }
    if (dove < 0) return false;

    const real = significativi[dove].at;
    const fine = significativi[Math.min(significativi.length - 1, dove + quanto - 1)].at + 1;

    const find = (offset) => {
      for (let index = nodes.length - 1; index >= 0; index -= 1) {
        if (nodes[index].start <= offset) {
          return { node: nodes[index].node, offset: offset - nodes[index].start };
        }
      }
      return null;
    };
    const from = find(real);
    const to = find(Math.min(flat.length - 1, fine));
    if (!from || !to) return false;

    const range = document.createRange();
    try {
      range.setStart(from.node, Math.min(from.offset, from.node.length));
      range.setEnd(to.node, Math.min(to.offset, to.node.length));
    } catch {
      return false;
    }
    const selection = window.getSelection?.();
    selection?.removeAllRanges();
    selection?.addRange(range);
    selectionRange.current = range.cloneRange();

    const block = (from.node.parentElement || root).closest("p,h1,h2,h3,h4,blockquote,li,pre") || from.node.parentElement;
    block?.scrollIntoView({ behavior: "smooth", block: "center" });
    if (block) {
      block.classList.add("numa-found");
      setTimeout(() => block.classList.remove("numa-found"), 2600);
    }
    return true;
  }, []);

  // A finding was clicked in the review: the content has to be on screen first.
  useEffect(() => {
    if (!passage?.at || !ref.current) return;
    const timer = setTimeout(() => {
      const found = showPassage(passage.excerpt);
      if (!found) {
        toast.info("Il passaggio non è stato trovato", {
          description: "Potrebbe essere stato modificato dopo la revisione.",
        });
      }
    }, 320);
    return () => clearTimeout(timer);
  }, [passage?.at, passage?.excerpt, showPassage, chapterId]);

  /**
   * Where the pages actually end.
   *
   * The previous attempt drew page frames on a separate layer at fixed
   * intervals, which drifted away from the text the moment the content was any
   * taller or shorter than assumed. This measures the rendered blocks instead
   * and marks the first block of each page: the mark is on the text itself, so
   * it cannot land anywhere the text is not. A block taller than a page - a
   * very long paragraph - simply overflows into the next one, exactly as it
   * would in Word before the paragraph is broken.
   *
   * The marks are attributes, not content: `data-numa-page` is not in the
   * sanitiser's allow-list, so it never reaches the saved document.
   */
  const measurePages = useCallback(() => {
    const root = columnRef.current;
    const surfaceList = root ? [...root.querySelectorAll(".numa-word-editor")] : [];
    if (!root || !surfaceList.length) return;

    const surfaceStyles = getComputedStyle(surfaceList[0]);
    const padTop = parseFloat(surfaceStyles.paddingTop) || 0;
    const padBottom = parseFloat(surfaceStyles.paddingBottom) || 0;
    const rootStyles = getComputedStyle(document.documentElement);
    const sheetHeight = parseFloat(rootStyles.getPropertyValue("--page-height")) || 1120;
    const gap = parseFloat(rootStyles.getPropertyValue("--page-gap")) || 28;
    const usable = Math.max(200, sheetHeight - padTop - padBottom);
    /*
      How far a paragraph may run into the bottom margin before being moved.

      A paragraph is never cut across two sheets - a browser cannot draw half
      an element on one sheet and half on the next - so one that does not fit
      moves down whole, and the page it leaves ends early. Allowing it to eat
      most of the bottom margin keeps the pages close to full, which keeps the
      page count close to what Word will print.
    */
    const tolerance = padBottom * 0.8;

    // Start from a clean slate: last pass's spacing would otherwise be read as
    // if it were the text's own, and the pages would creep downwards.
    for (const block of root.querySelectorAll("[data-numa-page]")) {
      block.style.marginTop = "";
      block.removeAttribute("data-numa-page");
    }
    // The chapters themselves carry the spacing between one sheet and the next.
    for (const surface of surfaceList) surface.style.marginTop = "";

    const rootTop = root.getBoundingClientRect().top;
    const boxOf = (element) => {
      const box = element.getBoundingClientRect();
      return { top: box.top - rootTop, bottom: box.top - rootTop + box.height };
    };

    /*
      Pages, chapter by chapter.

      Walking the chapters rather than one flat list of blocks is what gives an
      empty chapter a page of its own: it holds no text to measure, but it is
      still a sheet of paper, and without one it left a page-tall hole in the
      middle of the document with nothing drawn behind it.
    */
    const pages = [];
    for (const surface of surfaceList) {
      const blocks = [...surface.querySelectorAll("p,h1,h2,h3,h4,blockquote,li,pre,table")]
        .filter((el) => el.offsetHeight > 0 && !el.querySelector("p,h1,h2,h3,h4,blockquote,li,pre"));
      if (!blocks.length) {
        // An empty chapter opens a page too: it is a blank sheet, not a hole.
        pages.push({ surface, first: null, last: null, opens: true });
        continue;
      }
      // A chapter opens a page of its own, as it does in a bound volume.
      let pageTop = boxOf(blocks[0]).top;
      pages.push({ surface, first: blocks[0], last: blocks[0], opens: true });
      for (const block of blocks.slice(1)) {
        const { top, bottom } = boxOf(block);
        if (top > pageTop + 1 && bottom - pageTop > usable + tolerance) {
          pages.push({ surface, first: block, last: block });
          pageTop = top;
        } else {
          pages[pages.length - 1].last = block;
        }
      }
    }

    /*
      Each page is placed at a computed distance from the one before, rather
      than at a fixed margin: the space needed depends on how much of the
      previous page the text actually filled, and a page that ends early needs
      more of it, not less.

      The spacing is written on the element, so the browser lays the text out
      and we read the result back. `cleanForSave` strips it again before the
      chapter is stored: it is page furniture, not part of the document.
    */
    const rectangles = [];
    let sheetTop = Math.round(boxOf(pages[0].surface).top);
    pages.forEach((page, index) => {
      /*
        A chapter's first sheet starts where the chapter starts, not where its
        first paragraph does.

        Anything above that paragraph - the blank run an imported manuscript
        leaves before a part title, a picture, a frontispiece - is not a block
        this code measures, and anchoring the paper to the paragraph left all
        of it outside: a page-tall band of nothing between the toolbar and the
        first sheet. The chapter is moved as a whole instead, so that whatever
        it opens with is on the paper.
      */
      if (page.opens) {
        if (index > 0) {
          const own = parseFloat(getComputedStyle(page.surface).marginTop) || 0;
          const lead = sheetTop - boxOf(page.surface).top + own;
          page.surface.style.marginTop = `${Math.round(Math.max(0, lead))}px`;
        }
        const top = Math.round(boxOf(page.surface).top);
        if (!page.first) {
          // An empty chapter is still a sheet of paper.
          rectangles.push({ top, height: sheetHeight });
          sheetTop = top + sheetHeight + gap;
          return;
        }
        const bottom = boxOf(page.last).bottom + padBottom;
        const height = Math.max(sheetHeight, Math.round(bottom - top));
        rectangles.push({ top, height });
        sheetTop = top + height + gap;
        return;
      }
      page.first.setAttribute("data-numa-page", String(index + 1));
      // The inline margin replaces whatever the stylesheet gave this element -
      // a heading has its own - so that margin has to be added back, or the
      // block lands exactly that much too high and the gap between sheets
      // comes out short.
      const own = parseFloat(getComputedStyle(page.first).marginTop) || 0;
      const lead = sheetTop + padTop - boxOf(page.first).top + own;
      if (lead > 0) page.first.style.marginTop = `${Math.round(lead)}px`;
      const top = boxOf(page.first).top - padTop;
      const bottom = boxOf(page.last).bottom + padBottom;
      // A page taller than the paper is a single block that does not fit; it
      // gets the room it needs rather than being cut.
      const height = Math.max(sheetHeight, Math.round(bottom - top));
      rectangles.push({ top: Math.round(top), height });
      sheetTop = Math.round(top) + height + gap;
    });

    setSheets(rectangles);
    setPageCount(rectangles.length);
  }, []);

  /*
    Re-measured after the layout settles, and then once more.

    The second pass is not belt and braces: marking a block as the start of a
    page gives it the margin that separates the sheets, which moves everything
    below it. The first pass decides where the breaks are, the second reads the
    geometry once those margins are in place.
  */
  useEffect(() => {
    const timer = setTimeout(() => {
      measurePages();
      requestAnimationFrame(measurePages);
    }, 120);
    return () => clearTimeout(timer);
  }, [currentText, chapterId, measurePages]);

  // The sheet width follows the window, so the breaks move with it.
  useEffect(() => {
    const onResize = () => measurePages();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [measurePages]);

  /**
   * Keep the document made of blocks.
   *
   * Typing into an empty section puts a bare text node at the root of the
   * contenteditable, with no paragraph around it. Everything that formats a
   * paragraph - alignment, leading, indents - then has nothing to attach
   * itself to, and the controls do nothing at all for the first thing anyone
   * writes.
   */
  const ensureBlocks = () => {
    const root = ref.current;
    if (!root) return;
    const strays = [...root.childNodes].filter(
      (node) =>
        node.nodeType === 3
          ? node.textContent.trim()
          : node.nodeType === 1 && !BLOCK_TAGS.has(node.tagName)
    );
    if (!strays.length) return;

    const selection = window.getSelection?.();
    const offset = selection && selection.rangeCount && root.contains(selection.anchorNode)
      ? { node: selection.anchorNode, at: selection.anchorOffset }
      : null;

    for (const node of strays) {
      const paragraph = document.createElement("p");
      node.replaceWith(paragraph);
      paragraph.appendChild(node);
    }
    if (offset && offset.node.isConnected) {
      const range = document.createRange();
      range.setStart(offset.node, Math.min(offset.at, offset.node.length ?? offset.at));
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      selectionRange.current = range.cloneRange();
    }
  };

  /**
   * Every block the selection touches, not only the one holding the caret.
   *
   * Paragraph-level formatting applied to a three-paragraph selection has to
   * reach all three; using the anchor block alone silently formats one of them
   * and looks like the control is broken.
   */
  const selectedBlocks = () => {
    const root = ref.current;
    const selection = window.getSelection?.();
    const anchor = currentBlockElement();
    if (!root || !selection || !selection.rangeCount) return anchor ? [anchor] : [];
    const range = selection.getRangeAt(0);
    if (range.collapsed) return anchor ? [anchor] : [];

    const blocks = [...root.querySelectorAll("p,h1,h2,h3,h4,blockquote,li,pre,div")].filter(
      (element) => range.intersectsNode(element) && !element.querySelector("p,h1,h2,h3,h4,blockquote,li,pre")
    );
    return blocks.length ? blocks : anchor ? [anchor] : [];
  };

  const currentBlockElement = () => {
    const selection = window.getSelection?.();
    let node = selection?.anchorNode;
    while (node && node !== ref.current) {
      if (node.nodeType === 1 && /^(P|H1|H2|H3|H4|BLOCKQUOTE|LI|DIV|PRE)$/.test(node.tagName)) {
        return node;
      }
      node = node.parentNode;
    }
    return null;
  };

  const commitFormatting = () => {
    if (!ref.current) return;
    const updated = ref.current.innerHTML || "";
    setCurrentText(ref.current.innerText || "");
    persist(updated);
  };

  const applyAlignment = (value) => {
    if (!ref.current) return;
    ref.current.focus();
    ensureBlocks();
    restoreSelection();
    const command = {
      left: "justifyLeft", center: "justifyCenter",
      right: "justifyRight", justify: "justifyFull",
    }[value];
    if (command) cmd(command);
    setAlignment(value);
    commitFormatting();
  };

  /**
   * Line spacing has no execCommand, so it is written straight onto the block.
   * The exporter reads `line-height` back off the element, which is why the
   * setting survives into the DOCX instead of being screen-only decoration.
   */
  const applyLineSpacing = (value) => {
    if (!ref.current) return;
    ref.current.focus();
    ensureBlocks();
    restoreSelection();
    // Always written out, including "1". Clearing the property instead let the
    // block fall back to the editor stylesheet, which is looser than 1.15 -
    // so choosing single spacing visibly *increased* the spacing.
    for (const block of selectedBlocks()) block.style.lineHeight = value;
    setLineSpacing(value);
    commitFormatting();
  };

  /**
   * Font and size, written as inline style on the selection.
   *
   * `execCommand("fontSize")` only accepts the seven HTML sizes and emits a
   * `<font>` tag, which carries no point value and cannot survive into a DOCX.
   * A span with an explicit `font-size` in points is what Word actually means
   * by "12", and it is what the exporter reads back.
   */
  const applyInlineStyle = (property, value) => {
    if (!ref.current) return;
    ref.current.focus();
    ensureBlocks();
    restoreSelection();
    const selection = window.getSelection?.();
    if (!selection || !selection.rangeCount) return;
    const range = selection.getRangeAt(0);

    if (range.collapsed) {
      // No selection: the setting applies to the block the caret is in, which
      // is what Word does too.
      for (const block of selectedBlocks()) block.style[property] = value;
    } else {
      // Changing a setting on text that already carries one should edit that
      // span, not nest another inside it: three trips to the toolbar would
      // otherwise leave three wrappers around the same words.
      const existing =
        range.commonAncestorContainer.nodeType === 3
          ? range.commonAncestorContainer.parentElement
          : range.commonAncestorContainer;
      if (
        existing &&
        existing !== ref.current &&
        existing.tagName === "SPAN" &&
        existing.textContent === range.toString()
      ) {
        existing.style[property] = value;
        const updatedHtml = sanitizeHtml(cleanForSave(ref.current.innerHTML || ""));
        setCurrentText(ref.current.innerText || "");
        persist(updatedHtml);
        return;
      }

      const span = document.createElement("span");
      span.style[property] = value;
      try {
        range.surroundContents(span);
      } catch {
        // The selection crosses element boundaries; moving the contents works
        // where wrapping them in place does not.
        span.appendChild(range.extractContents());
        range.insertNode(span);
      }
      selection.removeAllRanges();
      const after = document.createRange();
      after.selectNodeContents(span);
      selection.addRange(after);
      selectionRange.current = after.cloneRange();
    }

    // The DOM is left as it is: rewriting innerHTML here would replace the very
    // nodes the selection points at, so the next setting in the toolbar would
    // have nothing to apply itself to.
    const updated = sanitizeHtml(cleanForSave(ref.current.innerHTML || ""));
    setCurrentText(ref.current.innerText || "");
    persist(updated);
  };

  const applyFontFamily = (value) => {
    setFontFamily(value);
    applyInlineStyle("fontFamily", value);
  };

  const applyFontSize = (value) => {
    setFontSize(value);
    applyInlineStyle("fontSize", `${value}pt`);
  };

  /**
   * Put a rewrite back into the document.
   *
   * A selection is replaced in place, so the surrounding formatting, the
   * paragraph it sits in and everything else on the page are left alone. Only
   * "whole section" replaces the body, and it keeps paragraph breaks.
   */
  const applyImprovedText = (text, scope) => {
    if (!ref.current || !text) return;
    ref.current.focus();

    if (scope === "selection" && restoreSelection()) {
      const selection = window.getSelection?.();
      if (selection && selection.rangeCount && !selection.isCollapsed) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(document.createTextNode(text));
        selection.collapseToEnd();
      }
    } else {
      ref.current.innerHTML = sanitizeHtml(
        text
          .split(/\n{2,}/)
          .map((paragraph) => paragraph.trim())
          .filter(Boolean)
          .map((paragraph) => `<p>${paragraph.replace(/</g, "&lt;").replace(/\n/g, "<br />")}</p>`)
          .join("")
      );
    }

    const updated = sanitizeHtml(cleanForSave(ref.current.innerHTML || ""));
    ref.current.innerHTML = updated;
    setCurrentText(ref.current.innerText || "");
    persist(updated);
    toast.success(
      scope === "selection" ? "Selezione sostituita" : "Sezione riscritta"
    );
  };

  /**
   * Quotation, as a toggle.
   *
   * `formatBlock` only ever wraps: pressing the button a second time on a
   * quotation left it a quotation, with no way back to body text except the
   * style dropdown, which is not where anyone looks for it.
   */
  const toggleQuote = () => {
    if (!ref.current) return;
    ref.current.focus();
    restoreSelection();
    const block = currentBlockElement();
    const isQuote = block?.tagName === "BLOCKQUOTE" || !!block?.closest?.("blockquote");
    cmd("formatBlock", isQuote ? "P" : "BLOCKQUOTE");
    setBlockStyle(isQuote ? "P" : "BLOCKQUOTE");
    commitFormatting();
  };

  /**
   * Highlight via `<mark>`.
   *
   * `execCommand("hiliteColor")` needs styleWithCSS enabled, silently does
   * nothing in some browsers, and produces a styled span that carries no
   * meaning. A `<mark>` element is semantic, is already styled by the editor
   * stylesheet, and is what the DOCX exporter maps to a highlighted run.
   */
  const toggleHighlight = () => {
    if (!ref.current) return;
    ref.current.focus();
    restoreSelection();
    const selection = window.getSelection?.();
    if (!selection || selection.isCollapsed) {
      toast.info("Seleziona il testo da evidenziare.");
      return;
    }

    // Already inside a mark: remove it, so the button toggles.
    let node = selection.anchorNode;
    while (node && node !== ref.current) {
      if (node.nodeType === 1 && node.tagName === "MARK") {
        const parent = node.parentNode;
        while (node.firstChild) parent.insertBefore(node.firstChild, node);
        parent.removeChild(node);
        commitFormatting();
        return;
      }
      node = node.parentNode;
    }

    const range = selection.getRangeAt(0);
    const mark = document.createElement("mark");
    try {
      range.surroundContents(mark);
    } catch {
      // The selection crosses element boundaries; extract and re-insert.
      mark.appendChild(range.extractContents());
      range.insertNode(mark);
    }
    selection.removeAllRanges();
    commitFormatting();
  };

  const getCursorContext = () => {
    const selection = window.getSelection?.();
    if (!selection || !selection.rangeCount || !ref.current || !ref.current.contains(selection.anchorNode)) return "";

    try {
      const before = selection.getRangeAt(0).cloneRange();
      before.selectNodeContents(ref.current);
      before.setEnd(selection.anchorNode, selection.anchorOffset);

      const after = selection.getRangeAt(0).cloneRange();
      after.selectNodeContents(ref.current);
      after.setStart(selection.anchorNode, selection.anchorOffset);

      return `${before.toString().slice(-900)}\n[CURSORE]\n${after.toString().slice(0, 350)}`.trim();
    } catch {
      return "";
    }
  };

  const getCurrentLineText = () => {
    const selection = window.getSelection?.();
    if (!selection || !selection.rangeCount || !ref.current || !ref.current.contains(selection.anchorNode)) return "";
    let node = selection.anchorNode;
    if (node?.nodeType === Node.TEXT_NODE) node = node.parentNode;
    while (node && node !== ref.current) {
      const tag = node.nodeName?.toLowerCase();
      if (["p", "div", "li", "h1", "h2", "h3", "blockquote"].includes(tag)) {
        return (node.innerText || node.textContent || "").replace(/\u00a0/g, " ").trim();
      }
      node = node.parentNode;
    }
    return "";
  };

  const buildStartingPrompt = () => {
    const cursorContext = getCursorContext();
    const title = chapter?.title ? `nel capitolo “${chapter.title}”` : "nel nuovo capitolo/paragrafo";
    return `Sono fermo su una nuova riga ${title}. Aiutami a capire da dove iniziare: proponimi una scaletta e l'ordine degli argomenti, senza scrivere il testo al posto mio.\n\nContesto vicino al cursore:\n${cursorContext || "(nessun testo vicino al cursore)"}`;
  };

  const handleInput = () => {
    saveCurrentSelection();
    const surface = ref.current;
    const typedIn = surface?.dataset?.chapterId || chapter?.id;
    const html = cleanForSave(surface?.innerHTML || "");
    const plain = surface?.innerText || "";
    const cursorContext = getCursorContext();
    setCurrentText(plain);
    setHelpBubble(false);
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    // Written synchronously, before the debounce: this is the copy that
    // survives a refresh or a crash in the 800 ms before the request goes out.
    if (thesis?.thesis_id && typedIn) {
      const named = (thesis.chapters || []).find((c) => c.id === typedIn);
      saveDraft(thesis.thesis_id, typedIn, html, named?.title || "");
      pendingHtml.current = html;
      setSaveState((s) => (s === "offline" ? "offline" : "unsaved"));
    }
    saveTimer.current = setTimeout(() => persist(html, typedIn), 800);
    if (citationTimer.current) clearTimeout(citationTimer.current);
    if (assistedWritingEnabled) {
      citationTimer.current = setTimeout(() => fetchCitationSuggestions(plain, cursorContext), 1800);
    }
  };

  const fetchCitationSuggestions = async (plain, cursorContext = "") => {
    if (!assistedWritingEnabled || !thesis?.thesis_id || !plain || plain.trim().length < 80) return;
    // Opportunistic: no model configured simply means no suggestions. Prompting
    // for an API key while someone is mid-sentence would be an interruption.
    if (!llmReady) return;
    setCitationLoading(true);
    try {
      const res = await numaApi.getCitationSuggestions({ thesis_id: thesis.thesis_id, context: plain.slice(-2500), cursor_text: cursorContext });
      setCitationSuggestions(res.suggestions || []);
    } catch {
      // Non blocchiamo la scrittura per i suggerimenti bibliografici.
    } finally {
      setCitationLoading(false);
    }
  };

  const syncToolbar = () => {
    setBlockStyle(currentBlockTag());
    const block = currentBlockElement();
    const align = block ? (window.getComputedStyle(block).textAlign || "left") : "left";
    setAlignment(["center", "right", "justify"].includes(align) ? align : "left");
    const height = block?.style?.lineHeight;
    setLineSpacing(LINE_SPACINGS.some((o) => o.value === height) ? height : DEFAULT_LINE_SPACING);
    setFontFamily(inlineValue("fontFamily") || DEFAULT_FONT);
    setFontSize(inlineValue("fontSize") || DEFAULT_SIZE);
  };

  /** The value actually in force at the caret, for the toolbar to show. */
  const inlineValue = (property) => {
    const selection = window.getSelection?.();
    let node = selection?.anchorNode;
    if (node && node.nodeType === 3) node = node.parentElement;
    while (node && node !== ref.current) {
      const own = node.style?.[property];
      if (own) return own;
      node = node.parentElement;
    }
    return "";
  };

  /**
   * A pause in writing, noticed once.
   *
   * Every section of NUMA is its own place, which is right, but it means the
   * help sitting one click away is invisible from the page where it is needed.
   * Five seconds of a still caret is the moment to mention it - quietly, once
   * per section, and never again if it is dismissed.
   */
  const noteActivity = () => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (helpBubbleSpent.current) return;
    idleTimer.current = setTimeout(() => {
      const focused = document.activeElement === ref.current;
      if (focused && !helpBubbleSpent.current) {
        helpBubbleSpent.current = true;
        setHelpBubble(true);
      }
    }, 5000);
  };

  const handleKeyUp = (e) => {
    saveCurrentSelection();
    syncToolbar();
    noteActivity();
  };

  const insertCitationAtCursor = (suggestion) => {
    if (!ref.current || !suggestion?.title) return;
    const safeTitle = escapeHtml(suggestion.title);
    const citationHtml = `<span class="numa-citation" data-pdf-id="${escapeHtml(suggestion.pdf_id || "")}"> (cfr. ${safeTitle})</span>&nbsp;`;

    ref.current.focus();
    const selection = window.getSelection?.();
    if (selectionRange.current && selection) {
      selection.removeAllRanges();
      selection.addRange(selectionRange.current);
      document.execCommand("insertHTML", false, citationHtml);
    } else {
      ref.current.innerHTML = sanitizeHtml(`${ref.current.innerHTML || ""}${citationHtml}`);
    }

    const updated = ref.current.innerHTML;
    setCurrentText(ref.current.innerText || "");
    setCitationSuggestions((prev) => prev.filter((s) => s !== suggestion));
    persist(updated);
    toast.success("Riferimento aggiunto nel punto di scrittura");
  };

  const acceptInlineSuggestion = (suggestion) => {
    if (!ref.current || !suggestion?.original || !suggestion?.suggestion) return;
    const html = ref.current.innerHTML || "";
    const escapedOriginal = suggestion.original.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    ref.current.innerHTML = sanitizeHtml(html.replace(new RegExp(escapedOriginal), suggestion.suggestion));
    const updated = ref.current.innerHTML;
    setCurrentText(ref.current.innerText || "");
    persist(updated);
  };

  if (!chapter) {
    return (
      <div className="h-screen flex items-center justify-center bg-surface p-8">
        <div className="bg-surface border border-line rounded-lg p-8 text-center max-w-md">
          <div className="flex justify-center mb-4">
            <NumaMascot size={80} className="" />
          </div>
          <h2 className="font-serif-display text-2xl font-semibold text-ink">Seleziona un capitolo</h2>
          <p className="text-sm text-ink-muted mt-2">Apri un capitolo dalla sidebar oppure crea un nuovo progetto per iniziare a scrivere.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="h-screen overflow-y-auto bg-surface" data-testid="editor-view">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 pt-6 lg:pt-10 pb-32">
          {/* Top metadata */}
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-5">
            <div>
              <h1 data-testid="thesis-title" className="font-serif-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink leading-[1.05]">
                {thesis?.title}
              </h1>
              <p className="text-base text-ink-muted mt-2" data-testid="thesis-subtitle">
                {chapter.title}{thesis?.subtitle ? ` · ${thesis.subtitle.replace(/^Capitolo \d+ - /, "")}` : ""}
              </p>
            </div>
            <div className="flex w-fit flex-wrap items-center gap-2">
            {/* Pages and words, counted from the text as it is laid out now. */}
            <div
              className="flex items-center gap-2 rounded-md border border-line bg-surface px-3 py-2 text-xs text-ink-muted"
              data-testid="page-counter"
            >
              <Layers className="h-3.5 w-3.5 text-ink-subtle" />
              <span className="tabular-nums">
                {pageCount} {pageCount === 1 ? "pagina" : "pagine"}
              </span>
              <span className="text-ink-subtle">·</span>
              <span className="tabular-nums">
                {currentText.trim() ? currentText.trim().split(/\s+/).length.toLocaleString("it-IT") : 0} parole
              </span>
            </div>
            <div
              className={`flex w-fit items-center gap-2 rounded-md border px-3 py-2 text-xs ${
                saveState === "offline" || saveState === "unsaved"
                  ? "border-warn/40 bg-warn/5 text-ink"
                  : "border-line bg-surface text-ink-muted"
              }`}
              data-testid="autosave-indicator"
              aria-live="polite"
            >
              {saveState === "saving" ? (
                <>
                  <span className="h-2 w-2 animate-pulse rounded-full bg-brand" />
                  <span>Salvataggio…</span>
                </>
              ) : saveState === "offline" ? (
                <>
                  <CloudOff className="h-3.5 w-3.5 text-warn" />
                  <span>Non in linea · modifiche conservate sul dispositivo</span>
                </>
              ) : saveState === "unsaved" ? (
                <>
                  <AlertCircle className="h-3.5 w-3.5 text-warn" />
                  <span>Non salvato</span>
                  <button
                    type="button"
                    onClick={retrySave}
                    className="font-medium text-brand underline underline-offset-2"
                  >
                    riprova
                  </button>
                </>
              ) : (
                <>
                  <Check className="h-3.5 w-3.5 text-ok" />
                  <span>
                    Salvato{savedAt ? ` · ${savedAt.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}` : ""}
                  </span>
                </>
              )}
            </div>
            </div>
          </div>

          <div className="mb-5 rounded-md border border-line bg-surface px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-md bg-assistant-soft flex items-center justify-center text-assistant flex-shrink-0">
                <WandSparkles className="w-4 h-4" />
              </div>
              <div>
                <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-ink">
                  Suggerimenti bibliografici automatici
                  <span className="rounded border border-assistant/30 bg-assistant-soft px-1.5 py-0.5 text-2xs font-normal text-assistant">
                    usa l&apos;AI
                  </span>
                </p>
                <p className="text-xs text-ink-muted mt-0.5">
                  Mentre scrivi, NUMA confronta il paragrafo con le fonti che hai caricato e,
                  quando ne trova una pertinente, te la propone. Sta a te decidere se citarla.
                  Sono disattivati finché non li accendi: servono un modello collegato e
                  consumano token a ogni paragrafo.
                </p>
              </div>
            </div>
            <button
              type="button"
              data-testid="assisted-writing-toggle"
              aria-pressed={assistedWritingEnabled}
              title={assistedWritingEnabled ? "Disattiva i suggerimenti" : "Attiva i suggerimenti"}
              onClick={async () => {
                // Turning them on is the moment a model becomes necessary, so
                // it is the moment to ask - not on arrival at the page.
                if (!assistedWritingEnabled && !(await ensureKey())) return;
                setAssistedWritingEnabled((v) => !v);
              }}
              className={`px-3 py-2 rounded-md text-xs font-medium border transition w-fit ${assistedWritingEnabled ? "bg-brand border-brand text-on-solid" : "bg-surface border-line text-ink-muted hover:border-line-strong"}`}
            >
              {assistedWritingEnabled ? "Attivi" : "Disattivati"}
            </button>
          </div>

          {/*
            The readings, on the section being written.

            They used to live in a rail on the right, which cost 340px of width
            and was the only place they existed. Here they are a row: visible,
            out of the way, and the same actions the review offers.
          */}
          <div className="mb-5 flex items-center gap-1.5 overflow-x-auto">
            <span className="numa-eyebrow shrink-0">Chiedi una lettura</span>
            {AI_READINGS.filter((a) => a.key !== "venue_fit").map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.key}
                  type="button"
                  data-testid={`editor-reading-${action.key}`}
                  onClick={() => onRunAi?.(action.key)}
                  title={action.desc}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-line bg-surface px-2.5 py-1.5 text-[12.5px] text-ink-muted transition-colors hover:border-assistant/50 hover:bg-assistant-soft/30 hover:text-ink"
                >
                  <Icon className="h-3.5 w-3.5 text-assistant" />
                  {action.short || action.title}
                </button>
              );
            })}
          </div>
          <AiBadge
            mode="optional"
            className="mb-5 -mt-3"
            detail="Scrivere, formattare e salvare non usano l'AI. Le letture qui sopra e i suggerimenti bibliografici inviano il testo della sezione al modello collegato."
          />

          {/* Offered when a local copy holds text the server never received. */}
          {recoverable && (
            <div
              data-testid="draft-recovery"
              className="mb-4 flex flex-wrap items-center gap-3 rounded-md border border-warn/40 bg-warn/5 px-4 py-3"
            >
              <RotateCcw className="h-4 w-4 shrink-0 text-warn" />
              <div className="min-w-0 flex-1">
                <p className="text-[13.5px] font-medium text-ink">
                  Ci sono modifiche non salvate su questo dispositivo
                </p>
                <p className="numa-meta mt-0.5">
                  Interrotte {describeAge(recoverable.savedAt)}, probabilmente da una disconnessione
                  o dalla chiusura della pagina. Il testo qui sopra è quello salvato sul server.
                </p>
              </div>
              <div className="flex shrink-0 gap-2">
                <button
                  type="button"
                  onClick={discardDraft}
                  className="rounded-md border border-line-strong px-3 py-1.5 text-[12.5px] text-ink-muted hover:bg-surface-2"
                >
                  Ignora
                </button>
                <button
                  type="button"
                  data-testid="recover-draft"
                  onClick={recoverDraft}
                  className="rounded-md bg-brand px-3 py-1.5 text-[12.5px] font-medium text-on-solid hover:bg-brand-hover"
                >
                  Ripristina
                </button>
              </div>
            </div>
          )}

          {/* Toolbar */}
          <div className="sticky top-2 lg:top-4 z-20 mb-5 overflow-x-auto">
            <div className="inline-flex items-center gap-0.5 bg-surface border border-line rounded-md shadow-sm px-2 py-2">
              <NumaSelect
                testId="tb-paragraph-style"
                aria-label="Stile del paragrafo"
                title="Stile del paragrafo, conservato nell'esportazione"
                value={blockStyle}
                onValueChange={applyBlockStyle}
                onPointerDown={saveCurrentSelection}
                className="mr-1 h-9 min-w-[7.5rem] text-[12.5px]"
                options={PARAGRAPH_STYLES.map((s) => ({ value: s.id, label: s.label }))}
              />
              <ToolbarButton testid="tb-new-paragraph" icon={CornerDownLeft} label="Nuovo paragrafo sotto" onClick={insertParagraphBelow} />
              <div className="w-px h-5 bg-line mx-1" />
              <ToolbarButton testid="tb-bold" icon={Bold} label="Grassetto" onClick={() => cmd("bold")} />
              <ToolbarButton testid="tb-italic" icon={Italic} label="Corsivo" onClick={() => cmd("italic")} />
              <ToolbarButton testid="tb-underline" icon={Underline} label="Sottolineato" onClick={() => cmd("underline")} />
              <div className="w-px h-5 bg-line mx-1" />
              <ToolbarButton testid="tb-highlight" icon={Highlighter} label="Evidenzia la selezione" onClick={toggleHighlight} />
              <div className="w-px h-5 bg-line mx-1" />
              <ToolbarButton testid="tb-align-left" icon={AlignLeft} label="Allinea a sinistra" onClick={() => applyAlignment("left")} active={alignment === "left"} />
              <ToolbarButton testid="tb-align-center" icon={AlignCenter} label="Centra" onClick={() => applyAlignment("center")} active={alignment === "center"} />
              <ToolbarButton testid="tb-align-right" icon={AlignRight} label="Allinea a destra" onClick={() => applyAlignment("right")} active={alignment === "right"} />
              <ToolbarButton testid="tb-align-justify" icon={AlignJustify} label="Giustifica" onClick={() => applyAlignment("justify")} active={alignment === "justify"} />
              <div className="w-px h-5 bg-line mx-1" />
              <NumaSelect
                testId="tb-font-family"
                aria-label="Carattere"
                title="Carattere"
                value={fontFamily}
                onValueChange={applyFontFamily}
                onPointerDown={saveCurrentSelection}
                className="h-9 min-w-[9rem] text-[12.5px]"
                options={FONTS}
              />
              <NumaSelect
                testId="tb-font-size"
                aria-label="Dimensione del testo"
                title="Dimensione del testo, in punti"
                value={fontSize}
                onValueChange={applyFontSize}
                onPointerDown={saveCurrentSelection}
                className="ml-1 h-9 min-w-[4.5rem] text-[12.5px]"
                options={FONT_SIZES.map((size) => ({ value: size, label: size }))}
              />
              <div className="w-px h-5 bg-line mx-1" />
              <NumaSelect
                testId="tb-line-spacing"
                aria-label="Interlinea"
                title="Interlinea"
                value={lineSpacing}
                onValueChange={applyLineSpacing}
                onPointerDown={saveCurrentSelection}
                className="h-9 min-w-[6.5rem] text-[12.5px]"
                options={LINE_SPACINGS}
              />
              <div className="w-px h-5 bg-line mx-1" />
              <ToolbarButton testid="tb-list" icon={List} label="Elenco puntato" onClick={() => cmd("insertUnorderedList")} />
              <ToolbarButton testid="tb-list-ordered" icon={ListOrdered} label="Elenco numerato" onClick={() => cmd("insertOrderedList")} />
              <ToolbarButton testid="tb-outdent" icon={Outdent} label="Riduci rientro" onClick={() => { cmd("outdent"); commitFormatting(); }} />
              <ToolbarButton testid="tb-indent" icon={Indent} label="Aumenta rientro" onClick={() => { cmd("indent"); commitFormatting(); }} />
              <ToolbarButton
                testid="tb-quote"
                icon={Quote}
                label="Citazione"
                onClick={toggleQuote}
                active={blockStyle === "BLOCKQUOTE"}
              />
              <button
                data-testid="tb-improve"
                onMouseDown={(e) => {
                  // preventDefault keeps the selection alive: the modal needs to
                  // know what the author had highlighted, and a plain click
                  // would have collapsed it before we could read it.
                  e.preventDefault();
                  saveCurrentSelection();
                  const selected = window.getSelection?.()?.toString() || "";
                  setImproveSelection(selected.trim() ? { text: selected } : null);
                  setImproveOpen(true);
                }}
                className="w-9 h-9 rounded-lg flex items-center justify-center text-ink-muted hover:bg-assistant-soft/40 hover:text-ink transition"
                title="Migliora testo"
              >
                <NumaLogo size={4} className="rounded" />
              </button>
            </div>
          </div>

          {/* Structure correction dropdown */}
          {chapter?.sections?.length > 0 && (
            <div className="relative mb-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setStructureMenuOpen(!structureMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium border border-line bg-surface text-ink-muted hover:border-line-strong hover:text-ink transition"
                >
                  <Layers className="w-3.5 h-3.5" />
                  Correggi struttura
                  <ChevronDown className={`w-3 h-3 transition ${structureMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                <span className="text-xs text-ink-muted">
                  {chapter.sections.length} sezioni · {chapter.sections.reduce((acc, s) => acc + (s.subsections?.length || 0), 0)} sottosezioni
                </span>
              </div>
              {structureMenuOpen && (
                <div className="absolute top-full left-0 mt-1 z-30 bg-surface border border-line rounded-md shadow-lg p-2 min-w-[220px]">
                  <button
                    onClick={() => restructureChapter("rename", prompt("Nuovo titolo:", chapter.title))}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-ink-muted hover:bg-surface-2 hover:text-ink transition"
                  >
                    <Type className="w-3.5 h-3.5" /> Rinomina
                  </button>
                  <button
                    onClick={() => restructureChapter("merge_with_previous")}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-ink-muted hover:bg-surface-2 hover:text-ink transition"
                  >
                    <Merge className="w-3.5 h-3.5" /> Unisci al capitolo precedente
                  </button>
                  <button
                    onClick={() => restructureChapter("promote_to_chapter")}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-ink-muted hover:bg-surface-2 hover:text-ink transition"
                  >
                    <ArrowUpDown className="w-3.5 h-3.5" /> Trasforma in capitolo
                  </button>
                  <button
                    onClick={() => restructureChapter("demote_to_section")}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-ink-muted hover:bg-surface-2 hover:text-ink transition"
                  >
                    <ArrowUpDown className="w-3.5 h-3.5" /> Trasforma in sezione
                  </button>
                </div>
              )}
            </div>
          )}

          {/*
            One continuous sheet.

            It used to draw page frames on an absolutely positioned layer under
            a freely flowing text. Nothing tied the two together, so as soon as
            the content was any taller or shorter than the frames they drifted
            apart and the text ran across headers, page numbers and the gaps
            between sheets. Real pagination means measuring and breaking the
            content into page boxes, which a contenteditable cannot do without
            a layout engine of its own; a page boundary that is genuinely part
            of the flow - an imported page break, or one the author inserts -
            cannot drift, because the text is what places it.
          */}
          <div className="numa-word-pages">
            {/* The sheets themselves, behind the text. Their edges come from
                the measurement, so they cannot drift away from it. */}
            <div className="numa-sheets" aria-hidden="true">
              {sheets.map((sheet, index) => (
                <div
                  key={index}
                  className="numa-sheet"
                  style={{ top: `${sheet.top}px`, height: `${sheet.height}px` }}
                >
                  <span className="numa-sheet-number">{index + 1}</span>
                </div>
              ))}
            </div>
            {/*
              One surface per chapter, stacked in one column.

              A document is read and written straight through: reaching the end
              of a chapter and having to go back to the sidebar to continue is
              not how anyone works on a thesis. The sidebar still jumps, but
              scrolling now carries on into the next chapter, and the caret
              moving between surfaces is what decides which chapter the toolbar
              and the panels are talking about.
            */}
            <div ref={columnRef} className="numa-column">
              {allChapters.map((item, index) => (
                <section key={item.id} className="numa-chapter" data-chapter-id={item.id}>
                  {index > 0 && (
                    <div className="numa-chapter-rule" aria-hidden="true">
                      <span>{item.title || "Senza titolo"}</span>
                    </div>
                  )}
                  <div
                    ref={(node) => {
                      surfaces.current[item.id] = node;
                      if (node && item.id === chapterId && !ref.current) ref.current = node;
                    }}
                    data-testid={item.id === chapterId ? "editor-content" : `editor-content-${item.id}`}
                    data-chapter-id={item.id}
                    contentEditable
                    suppressContentEditableWarning
                    // The browser's own dictionary, in Italian: free, offline,
                    // and the right tool for a typo while it is being typed.
                    spellCheck
                    lang="it"
                    onInput={() => { ensureBlocks(); handleInput(); noteActivity(); }}
                    onKeyUp={handleKeyUp}
                    onMouseUp={() => { saveCurrentSelection(); syncToolbar(); noteActivity(); }}
                    onClick={saveCurrentSelection}
                    onFocus={(event) => {
                      // The caret decides the active chapter, not the sidebar.
                      ref.current = event.currentTarget;
                      if (item.id !== chapterId) onChapterFocus?.(item.id);
                      noteActivity();
                    }}
                    onBlur={() => { if (idleTimer.current) clearTimeout(idleTimer.current); }}
                    data-placeholder={index === 0 ? "Inizia a scrivere…" : ""}
                    className="numa-editor numa-word-editor"
                  />
                </section>
              ))}
            </div>
          </div>

          {helpBubble && (
            <div
              data-testid="idle-help-bubble"
              className="numa-help-bubble fixed bottom-24 right-6 z-40 flex max-w-[280px] items-start gap-2.5 rounded-md border border-line bg-surface px-3.5 py-3 shadow-lg"
            >
              <NumaMascot size={30} className="shrink-0" variant={5} />
              <div className="min-w-0">
                <p className="text-[13px] leading-snug text-ink">
                  Ti serve una mano per partire?
                </p>
                <button
                  type="button"
                  data-testid="idle-help-open"
                  onClick={() => { setHelpBubble(false); onNeedHelp?.(buildStartingPrompt()); }}
                  className="mt-1 text-[12.5px] font-medium text-brand hover:underline"
                >
                  Apri «Da dove iniziare»
                </button>
              </div>
              <button
                type="button"
                aria-label="Chiudi"
                onClick={() => setHelpBubble(false)}
                className="-mr-1 -mt-1 shrink-0 rounded p-1 text-ink-subtle hover:bg-surface-2 hover:text-ink"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}

          {assistedWritingEnabled && (citationSuggestions.length > 0 || citationLoading) && (
            <div data-testid="citation-suggestions" className="fixed left-1/2 -translate-x-1/2 bottom-6 z-40 w-[min(680px,calc(100vw-2rem))] bg-surface border border-line rounded-lg shadow-2xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-md bg-assistant-soft flex items-center justify-center text-assistant flex-shrink-0">
                    {citationLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <BookOpen className="w-5 h-5" />}
                  </div>
                  <div>
                    <p className="font-serif-display font-semibold text-ink">Scrittura assistita</p>
                    <p className="text-xs text-ink-muted">NUMA controlla le fonti mentre scrivi nel punto vicino al cursore.</p>
                  </div>
                </div>
                <button onClick={() => setCitationSuggestions([])} className="p-1 rounded-lg hover:bg-surface-2">
                  <X className="w-4 h-4 text-ink-muted" />
                </button>
              </div>
              {citationLoading && citationSuggestions.length === 0 ? (
                <p className="text-sm text-ink-muted mt-3">Sto cercando la fonte più adatta...</p>
              ) : (
                <div className="mt-3 space-y-2">
                  {citationSuggestions.map((s, idx) => (
                    <div key={`${s.pdf_id}-${idx}`} className="rounded-md bg-paper border border-line p-3">
                      <p className="text-sm text-ink"><strong>Qui ci starebbe bene questo:</strong> {s.title}</p>
                      <p className="text-xs text-ink-muted mt-1">{s.reason}</p>
                      <p className="text-xs text-assistant mt-1">Che ne pensi, lo aggiungiamo? {s.insertion_hint}</p>
                      <div className="mt-3 flex flex-wrap gap-2 justify-end">
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => setCitationSuggestions((prev) => prev.filter((_, i) => i !== idx))}
                          className="px-3 py-1.5 rounded-md border border-line text-xs text-ink-muted hover:bg-surface transition"
                        >
                          No grazie
                        </button>
                        <button
                          type="button"
                          data-testid={`accept-citation-${idx}`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => insertCitationAtCursor(s)}
                          className="px-3 py-1.5 rounded-md bg-brand text-on-solid text-xs font-medium hover:bg-brand-hover transition inline-flex items-center gap-1.5"
                        >
                          <PlusCircle className="w-3.5 h-3.5" /> Aggiungi riferimento
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <ImproveTextModal
        open={improveOpen}
        onOpenChange={setImproveOpen}
        chapterTitle={chapter?.title || ""}
        projectType={thesis?.project_type}
        selection={improveSelection}
        wholeText={currentText}
        onApply={applyImprovedText}
      />
      <InlineSuggestionsPanel text={currentText} onAcceptSuggestion={acceptInlineSuggestion} />
    </>
  );
}
